import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { Conversation, ConversationContent, ConversationScrollButton } from "@/components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import {
  PromptInput, PromptInputFooter, PromptInputSubmit, PromptInputTextarea,
} from "@/components/ai-elements/prompt-input";
import logoFull from "@/assets/logo-full-white-transparent.png";
import logoSymbol from "@/assets/logo-symbol-white-transparent.png";
import { buildLeadMagnetFields, formatUsPhone, isValidEmail, postFn, toUsE164 } from "@/components/LeadMagnetPage";
import { SCRIPT_MARKER, splitScript, textOf } from "@/lib/script-builder";

const TAG = "lm-ad-script-builder";
const PATH = "/lm/ad-script-builder";
const TOOL = "ad-script-builder";
const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/script-chat`;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const START_TEXT = "Start.";
const inputCls = "w-full rounded-lg border border-[#03214B]/20 bg-white px-4 py-3 text-base text-[#03214B] placeholder:text-[#03214B]/40 focus:border-[#FFB002] focus:outline-none focus:ring-2 focus:ring-[#FFB002]/40";

const ContactFields = ({ v, set, withNames }: {
  v: { name: string; company: string; phone: string; email: string };
  set: (k: "name" | "company" | "phone" | "email", val: string) => void;
  withNames: boolean;
}) => (
  <div className="space-y-3">
    {withNames && (<>
      <label className="block">
        <span className="mb-1 block text-sm font-semibold">Full name</span>
        <input className={inputCls} value={v.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" required maxLength={120} />
      </label>
      <label className="block">
        <span className="mb-1 block text-sm font-semibold">Company name</span>
        <input className={inputCls} value={v.company} onChange={(e) => set("company", e.target.value)} autoComplete="organization" required maxLength={120} />
      </label>
    </>)}
    <label className="block">
      <span className="mb-1 block text-sm font-semibold">Phone number</span>
      <input className={inputCls} type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 555-5555"
        value={v.phone} onChange={(e) => set("phone", formatUsPhone(e.target.value))} required />
      <span className="mt-1 block text-xs text-[#03214B]/60">We'll text you the resource.</span>
    </label>
    <label className="block">
      <span className="mb-1 block text-sm font-semibold">Email</span>
      <input className={inputCls} type="email" inputMode="email" autoComplete="email" placeholder="you@company.com"
        value={v.email} onChange={(e) => set("email", e.target.value)} required maxLength={255} />
      <span className="mt-1 block text-xs text-[#03214B]/60">We'll also email it to you.</span>
    </label>
  </div>
);

const LmAdScriptBuilder = () => {
  const sessionId = useMemo(() => crypto.randomUUID(), []);
  const transport = useMemo(() => new DefaultChatTransport({
    api: CHAT_URL,
    headers: { apikey: KEY, Authorization: `Bearer ${KEY}` },
    body: { session_id: sessionId, tool: TOOL },
  }), [sessionId]);
  const { messages, sendMessage, status, error, stop } = useChat({ transport });
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    sendMessage({ text: START_TEXT });
  }, [sendMessage]);

  // Save the conversation (even unfinished) after every completed AI reply.
  const synced = useRef(0);
  useEffect(() => {
    if (status !== "ready" || messages.length < 2 || messages.length === synced.current) return;
    if (messages[messages.length - 1].role !== "assistant") return;
    synced.current = messages.length;
    const q = new URLSearchParams(window.location.search);
    postFn("script-session-sync", {
      session_id: sessionId,
      messages: messages.map((m) => ({ role: m.role, text: textOf(m) })),
      utm_source: q.get("utm_source") ?? "", utm_campaign: q.get("utm_campaign") ?? "",
    }).catch(() => {});
  }, [status, messages, sessionId]);

  const [v, setV] = useState({ name: "", company: "", phone: "", email: "" });
  const set = (k: keyof typeof v, val: string) => setV((p) => ({ ...p, [k]: val }));
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);
  const [scriptId, setScriptId] = useState<string | null>(null);
  const [resend, setResend] = useState(false);

  const visible = messages.filter((m, i) => !(i === 0 && m.role === "user"));
  const last = messages[messages.length - 1];
  const lastText = last?.role === "assistant" ? textOf(last) : "";
  const scriptMarkdown = lastText.trimStart().startsWith(SCRIPT_MARKER) && status === "ready" ? splitScript(lastText).full : null;
  const isStreamingScript = lastText.trimStart().startsWith("[SCRIPT");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!resend && (!v.name.trim() || !v.company.trim())) return setFormError("Please fill in your name and company.");
    const e164 = toUsE164(v.phone);
    if (!e164) return setFormError("Please enter a valid US phone number.");
    if (!isValidEmail(v.email)) return setFormError("Please enter a valid email address.");
    setSending(true);
    const session_id = crypto.randomUUID();
    const fields = buildLeadMagnetFields(TAG, v.name, v.company, e164, window.location.search, window.location.href, v.email);
    try {
      const res = await postFn("qualification-submit", {
        session_id, fields, lead_magnet: TOOL, chat_session_id: sessionId,
        ...(scriptId ? { script_id: scriptId } : { script_markdown: scriptMarkdown }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json().catch(() => ({}));
      if (data?.script_id) setScriptId(data.script_id);
      postFn("vsl-lead-sync", { session_id, fields: { ...fields, status: "qualified", last_step: TAG } }).catch(() => {});
      const w = window as unknown as { dataLayer?: unknown[] };
      (w.dataLayer = w.dataLayer || []).push({ event: "lm_submit", lead_magnet: TAG });
      setResend(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setFormError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const vslHref = `/vsl${typeof window !== "undefined" ? window.location.search : ""}`;
  const done = !!scriptId && !resend;

  return (
    <main className="min-h-screen bg-[#03214B] px-5 py-6 text-white md:py-12">
      <Helmet>
        <title>Free AI Video Ad Script Writer | The Alpha Omega Hub</title>
        <meta name="description" content="Free AI tool for home-service businesses: answer 6 questions and get a 45-60 second bottom-of-funnel video ad script, 5 hooks and a B-roll shot list, ready to film with your phone." />
        <link rel="canonical" href={`https://thealphaomegahub.com${PATH}`} />
      </Helmet>
      <div className="mx-auto max-w-3xl">
        <img src={logoFull} alt="The Alpha Omega Hub" className="mx-auto h-10 w-auto md:h-12" />

        {done ? (
          <section className="mx-auto mt-14 max-w-xl rounded-2xl bg-white p-8 text-center text-[#03214B] shadow-xl md:p-10">
            <h1 className="font-display text-2xl font-bold md:text-3xl">
              Done! We texted your script to {formatUsPhone(v.phone)} and emailed it to {v.email.trim().toLowerCase()}.
            </h1>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button type="button" onClick={() => { setResend(true); setV((p) => ({ ...p, phone: "", email: "" })); }}
                className="rounded-lg border-2 border-[#03214B] px-5 py-3 font-semibold text-[#03214B] transition hover:bg-[#03214B]/5">
                Send it to another number
              </button>
              <a href={vslHref} className="rounded-lg bg-[#FFB002] px-5 py-3 font-bold text-[#03214B] transition hover:brightness-105">
                Click here to view our services
              </a>
            </div>
          </section>
        ) : resend ? (
          <form onSubmit={submit} noValidate className="mx-auto mt-14 max-w-xl rounded-2xl bg-white p-6 text-[#03214B] shadow-xl md:p-8">
            <h1 className="mb-4 font-display text-2xl font-bold">Send your script to another number</h1>
            <ContactFields v={v} set={set} withNames={false} />
            {formError && <p className="mt-4 text-sm font-medium text-red-600" role="alert">{formError}</p>}
            <button type="submit" disabled={sending} className="mt-5 w-full rounded-lg bg-[#FFB002] px-6 py-4 text-lg font-bold text-[#03214B] disabled:opacity-60">
              {sending ? "Sending…" : "Send my script"}
            </button>
          </form>
        ) : (
          <>
            <header className="mt-4 text-center md:mt-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFB002] md:text-sm">FREE AI TOOL</p>
              <h1 className="mx-auto mt-3 max-w-3xl font-display text-3xl font-bold leading-tight md:text-5xl">Answer 6 questions and get your video ad script now</h1>
              <p className="mx-auto mt-4 max-w-2xl text-base text-white/80 md:text-lg">
                Our AI writes your bottom-of-funnel video ad for your business: the 7-block body and 5 hooks, ready to film with your phone.
              </p>
            </header>

            <section className="mt-6 overflow-hidden rounded-2xl bg-white text-[#03214B] shadow-xl md:mt-10">
              {scriptMarkdown ? (
                <div className="relative p-6 md:p-8">
                  <div className="pointer-events-none select-none" aria-hidden>
                    <MessageResponse>{splitScript(scriptMarkdown).preview}</MessageResponse>
                    <div className="mt-2 blur-[6px]"><MessageResponse>{splitScript(scriptMarkdown).rest}</MessageResponse></div>
                  </div>
                  <form onSubmit={submit} noValidate
                    className="absolute inset-x-4 top-40 rounded-2xl border border-[#03214B]/10 bg-white p-6 shadow-2xl md:inset-x-12 md:p-8">
                    <h2 className="mb-4 font-display text-xl font-bold md:text-2xl">Your script is ready. We'll text it to you and email it.</h2>
                    <ContactFields v={v} set={set} withNames />
                    {formError && <p className="mt-4 text-sm font-medium text-red-600" role="alert">{formError}</p>}
                    <button type="submit" disabled={sending} className="mt-5 w-full rounded-lg bg-[#FFB002] px-6 py-4 text-lg font-bold text-[#03214B] disabled:opacity-60">
                      {sending ? "Sending…" : "Send me my script"}
                    </button>
                  </form>
                  <div className="h-[420px] md:h-[360px]" />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 border-b border-[#03214B]/10 px-5 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#03214B]">
                      <img src={logoSymbol} alt="" className="h-5 w-auto" />
                    </span>
                    <div>
                      <p className="text-sm font-bold">TAOH Ad Script Coach</p>
                      <p className="text-xs text-[#03214B]/60">AI-powered · 6 questions</p>
                    </div>
                  </div>
                  <Conversation className="h-[60vh] min-h-[420px]">
                    <ConversationContent>
                      {visible.map((m) => {
                        const t = textOf(m);
                        if (m.role === "assistant" && t.trimStart().startsWith("[SCRIPT")) {
                          return <p key={m.id} className="text-sm font-semibold text-[#03214B]/70">Building your script…</p>;
                        }
                        return (
                          <Message key={m.id} from={m.role}>
                            <MessageContent className={m.role === "user" ? "bg-[#03214B] text-white" : ""}>
                              {m.role === "assistant" ? <MessageResponse>{t}</MessageResponse> : <p className="whitespace-pre-wrap">{t}</p>}
                            </MessageContent>
                          </Message>
                        );
                      })}
                      {status === "submitted" && <p className="text-sm text-[#03214B]/60">Thinking…</p>}
                      {error && <p className="text-sm font-medium text-red-600" role="alert">{(() => { try { return JSON.parse(error.message).error; } catch { return "Something went wrong. Please try again."; } })()}</p>}
                    </ConversationContent>
                    <ConversationScrollButton />
                  </Conversation>
                  <div className="border-t border-[#03214B]/10 p-3">
                    <PromptInput onSubmit={({ text }) => { if (text.trim()) sendMessage({ text }); }}>
                      <PromptInputTextarea placeholder={isStreamingScript ? "Building your script…" : "Type your answer…"} disabled={isStreamingScript} />
                      <PromptInputFooter className="justify-end">
                        <PromptInputSubmit status={status} onStop={stop} className="bg-[#FFB002] text-[#03214B] hover:bg-[#FFB002]/90" />
                      </PromptInputFooter>
                    </PromptInput>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default LmAdScriptBuilder;
